import { domainError, type DomainError } from '../shared/domain-error';
import { compareNames, normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { MaterialId, UnitOfMeasureId } from '../shared/ids';
import { SCHEDULE_ERROR, SCHEDULE_ERROR_MESSAGE } from './errors';

/**
 * Material — entidade de catálogo, nunca texto livre (RN-010).
 *
 * Sem catálogo, "Tijolo 19x30", "tijolo 19 x 30" e "TIJOLO19X30" viram três necessidades
 * distintas e suprimentos compra três vezes. O catálogo é **global**, compartilhado entre
 * obras (ADR-020): material é insumo de mercado, não da obra. O que é por obra são a
 * quantidade e a data — que vivem no item do cronograma.
 */
export interface Material {
  readonly id: MaterialId;
  /** Único no sistema inteiro, comparado sem diferenciar maiúsculas (RN-011). */
  readonly code: string;
  readonly name: string;
  readonly description?: string;
  /** Unidade padrão. O item pode divergir dela — vira aviso, não erro (RN-012). */
  readonly unitId: UnitOfMeasureId;
  readonly category?: string;
  readonly isActive: boolean;
  /**
   * O código é o provisório `ORC-…` do orçamento, sem SKU de compras (ADR-167). Ausente é
   * falso. O material assim marcado não vira requisição até ser vinculado (RN-347).
   */
  readonly codeIsProvisional?: boolean;
  /**
   * O código `ORC-…` que o material tinha antes de alguém vinculá-lo ao SKU (RN-347).
   * Presente só no material vinculado: é o registro do vínculo, e é por ele — e nunca pelo
   * nome — que a reimportação da planilha do orçamento acha o material de novo.
   */
  readonly budgetCode?: string;
  /**
   * MATERIAL é compra; SERVICO_TERCEIRIZADO é contratação de empreiteiro (ADR-172,
   * RN-349). Ausente é MATERIAL — o catálogo inteiro até a Fase 26.
   */
  readonly kind?: MaterialKind;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

/** O espelho, no domínio, de `PurchaseKind` (`contracts/purchase-lines.ts`). */
export const MATERIAL_KINDS = ['MATERIAL', 'SERVICO_TERCEIRIZADO'] as const;
export type MaterialKind = (typeof MATERIAL_KINDS)[number];

/** O prefixo do código provisório do orçamento (ADR-167). */
export const PROVISIONAL_MATERIAL_CODE_PREFIX = 'ORC-';

/** `ORC-0042` é código provisório; `ACO-CA50-10`, não. Sem diferenciar caixa nem espaço. */
export function isProvisionalMaterialCode(code: string): boolean {
  return code.trim().toUpperCase().startsWith(PROVISIONAL_MATERIAL_CODE_PREFIX);
}

/** RN-347 — o que não pode virar requisição: o material com código provisório. */
export function hasProvisionalCode(material: Pick<Material, 'codeIsProvisional'>): boolean {
  return material.codeIsProvisional === true;
}

export function materialKindOf(material: Pick<Material, 'kind'>): MaterialKind {
  return material.kind ?? 'MATERIAL';
}

export interface CreateMaterialInput {
  readonly id: MaterialId;
  readonly code: string;
  readonly name: string;
  readonly unitId: UnitOfMeasureId;
  readonly description?: string;
  readonly category?: string;
  /**
   * Só a importação das linhas de compra passa: o material que nasce com código `ORC-…`
   * nasce provisório (ADR-167). Quem digita um código na tela digita o SKU.
   */
  readonly codeIsProvisional?: boolean;
  readonly kind?: MaterialKind;
  readonly now: string;
}

export function createMaterial(input: CreateMaterialInput): Result<Material, DomainError> {
  const code = input.code.trim();
  const name = input.name.trim();

  if (code.length === 0) {
    return err(
      domainError(SCHEDULE_ERROR.MATERIAL_NOT_FOUND, 'O código do material é obrigatório.'),
    );
  }
  if (name.length === 0) {
    return err(domainError(SCHEDULE_ERROR.MATERIAL_NOT_FOUND, 'O nome do material é obrigatório.'));
  }

  return ok({
    id: input.id,
    code,
    name,
    unitId: input.unitId,
    isActive: true,
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.description === undefined ? {} : { description: input.description }),
    ...(input.category === undefined ? {} : { category: input.category }),
    // RN-347 — a marca só vale para o `ORC-…`: um SKU marcado como provisório nunca
    // viraria requisição, e ninguém saberia por quê (o banco recusa o mesmo).
    ...(input.codeIsProvisional === true && isProvisionalMaterialCode(code)
      ? { codeIsProvisional: true }
      : {}),
    ...(input.kind === undefined || input.kind === 'MATERIAL' ? {} : { kind: input.kind }),
  });
}

/**
 * Vincula o material de código provisório ao SKU do sistema de compras (RN-347).
 *
 * O código passa a ser o SKU, a marca de provisório sai e o `ORC-…` fica guardado em
 * `budgetCode`. O id não muda: as linhas do cronograma que já apontam para o material
 * continuam apontando, e passam a poder virar requisição. Que nenhum outro material use o
 * SKU é conferido por quem grava — a chave é global (RN-011).
 */
export function linkMaterialToSku(
  material: Material,
  sku: string,
  now: string,
): Result<Material, DomainError> {
  if (!hasProvisionalCode(material)) {
    return err(
      domainError(
        SCHEDULE_ERROR.MATERIAL_NOT_PROVISIONAL,
        SCHEDULE_ERROR_MESSAGE[SCHEDULE_ERROR.MATERIAL_NOT_PROVISIONAL]!,
        { code: material.code },
      ),
    );
  }
  const code = sku.trim();
  if (code === '' || isProvisionalMaterialCode(code)) {
    return err(
      domainError(
        SCHEDULE_ERROR.MATERIAL_SKU_INVALID,
        SCHEDULE_ERROR_MESSAGE[SCHEDULE_ERROR.MATERIAL_SKU_INVALID]!,
        { sku: code },
      ),
    );
  }
  const { codeIsProvisional: _provisorio, budgetCode: _anterior, ...resto } = material;
  return ok({ ...resto, code, budgetCode: material.code.trim(), updatedAt: now });
}

/** Chave natural do material: o código, normalizado (RN-011). */
export function materialNaturalKey(code: string): string {
  return normalizeName(code);
}

export function materialCodeTakenError(code: string): DomainError {
  return domainError(
    SCHEDULE_ERROR.MATERIAL_CODE_TAKEN,
    SCHEDULE_ERROR_MESSAGE[SCHEDULE_ERROR.MATERIAL_CODE_TAKEN]!,
    { code },
  );
}

export function compareMaterials(a: Material, b: Material): number {
  return compareNames(a.name, b.name);
}

/**
 * Catálogo semeado no bootstrap — o suficiente para operar uma obra residencial sem
 * cadastrar nada antes de começar. `unit` referencia o `code` da unidade.
 */
export const DEFAULT_MATERIALS: readonly {
  code: string;
  name: string;
  unit: string;
  category: string;
}[] = [
  { code: 'CIM-CP2', name: 'Cimento CP-II 50kg', unit: 'sc', category: 'Aglomerantes' },
  { code: 'CAL-HID', name: 'Cal hidratada 20kg', unit: 'sc', category: 'Aglomerantes' },
  { code: 'ARE-MED', name: 'Areia média', unit: 'm3', category: 'Agregados' },
  { code: 'BRI-1', name: 'Brita 1', unit: 'm3', category: 'Agregados' },
  { code: 'CON-25', name: 'Concreto usinado 25 MPa', unit: 'm3', category: 'Concreto' },
  { code: 'ACO-CA50-10', name: 'Aço CA-50 10mm', unit: 'kg', category: 'Aço' },
  { code: 'ACO-CA50-12', name: 'Aço CA-50 12,5mm', unit: 'kg', category: 'Aço' },
  { code: 'ACO-CA60-5', name: 'Aço CA-60 5mm', unit: 'kg', category: 'Aço' },
  { code: 'FOR-MAD', name: 'Fôrma de madeira compensada', unit: 'm2', category: 'Fôrmas' },
  { code: 'ESC-MET', name: 'Escora metálica', unit: 'un', category: 'Fôrmas' },
  { code: 'TIJ-19X30', name: 'Bloco cerâmico 19x30', unit: 'un', category: 'Alvenaria' },
  { code: 'BLO-CON-14', name: 'Bloco de concreto 14x19x39', unit: 'un', category: 'Alvenaria' },
  { code: 'ARG-AC3', name: 'Argamassa colante AC-III 20kg', unit: 'sc', category: 'Argamassas' },
  { code: 'ARG-REJ', name: 'Rejunte 5kg', unit: 'sc', category: 'Argamassas' },
  { code: 'VER-GES', name: 'Gesso para revestimento', unit: 'sc', category: 'Revestimentos' },
  { code: 'PIS-POR-60', name: 'Porcelanato 60x60', unit: 'm2', category: 'Revestimentos' },
  { code: 'TIN-ACR', name: 'Tinta acrílica 18L', unit: 'L', category: 'Pintura' },
  { code: 'MAS-COR', name: 'Massa corrida 18L', unit: 'L', category: 'Pintura' },
  { code: 'IMP-MAN', name: 'Manta asfáltica 4mm', unit: 'm2', category: 'Impermeabilização' },
  { code: 'ELE-CAB-25', name: 'Cabo flexível 2,5mm²', unit: 'm', category: 'Elétrica' },
  { code: 'ELE-CAB-40', name: 'Cabo flexível 4,0mm²', unit: 'm', category: 'Elétrica' },
  { code: 'ELE-ELD-25', name: 'Eletroduto flexível 25mm', unit: 'm', category: 'Elétrica' },
  {
    code: 'ELE-QDR',
    name: 'Quadro de distribuição 12 disjuntores',
    unit: 'un',
    category: 'Elétrica',
  },
  { code: 'HID-PVC-25', name: 'Tubo PVC soldável 25mm', unit: 'm', category: 'Hidráulica' },
  { code: 'HID-PVC-100', name: 'Tubo PVC esgoto 100mm', unit: 'm', category: 'Hidráulica' },
  { code: 'HID-CON-25', name: 'Conexões PVC 25mm', unit: 'cj', category: 'Hidráulica' },
  { code: 'ESQ-JAN-AL', name: 'Janela de alumínio 1,20x1,00', unit: 'un', category: 'Esquadrias' },
  { code: 'ESQ-POR-MAD', name: 'Porta de madeira 0,80x2,10', unit: 'un', category: 'Esquadrias' },
];
